import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { MODAL_ROUTES, ROUTES } from "routing/routes";

import {
  Box,
  ContextMenuButton,
  ContextMenuLink,
  Divider,
  Icon,
  Stack,
  Text
} from "@seneca/senekit";

import { linkToHealthResourcesArticle } from "common/links";

import { openWindowSecurely } from "seneca-common/utils/routing";

import DarkModeMenuButton from "features/dark-mode/components/DarkModeMenuButton";
import {
  linkToDashboard,
  linkToDashboardFeedback,
  linkToDashboardInviteParentsModal,
  linkToStudentHelpSection
} from "features/dashboard/links";
import { linkToCoursesPage } from "features/dashboard/pages/courses-page/links";
import { linkToSettings } from "features/dashboard/pages/settings-page/links";
import { NavbarSideDrawer } from "features/navbar";
import { getLinkToPlurallStore } from "features/plurall/links";
import { analyticsLogUserClickedActivateTutoringNavbar } from "features/tutoring/activation-journey/analytics";
import { setPlurallHelpCentreModalOpen } from "features/ui-state/actions";

export default function TabletNavbar({
  email,
  showRegionLink,
  locale,
  signOut,
  sellPremiumToUser,
  sellUpgradeToUser,
  isTeacher,
  updateAppVersion,
  appVersionUpdateReady,
  appVersionIsUpdating,
  hasTutoringInactive,
  isPlurallApp,
  linkToTutoringActivation,
  linkToWhatIsPremium,
  GetPremium,
  UpgradePremium,
  ActivateTutoring
}: any) {
  const dispatch = useDispatch();
  const location = useLocation();

  return (
    <NavbarSideDrawer>
      {{
        options: (closeDrawer: any) => (
          <Stack space="large">
            {!isPlurallApp && (
              <>
                <Box margin="large">
                  <Text dataNoTranslate size="small" tone="neutral" truncate>
                    {email}
                  </Text>
                </Box>

                <Divider direction="horizontal" />
              </>
            )}

            <Stack>
              <ContextMenuLink onClick={closeDrawer} to={linkToDashboard()}>
                <Text>Home</Text>
              </ContextMenuLink>
              <ContextMenuLink onClick={closeDrawer} to={linkToCoursesPage()}>
                <Text>Courses</Text>
              </ContextMenuLink>
              <ContextMenuLink
                onClick={closeDrawer}
                to={ROUTES.DASHBOARD.ASSIGNMENTS.buildPath({})}
              >
                <Text>Assignments</Text>
              </ContextMenuLink>
              <ContextMenuLink
                onClick={closeDrawer}
                to={ROUTES.DASHBOARD.CLASSES.buildPath({})}
              >
                <Text>Classes</Text>
              </ContextMenuLink>
              {!isPlurallApp && !isTeacher && sellPremiumToUser && (
                <ContextMenuLink onClick={closeDrawer} to={linkToWhatIsPremium}>
                  <Text>
                    <GetPremium />
                  </Text>
                </ContextMenuLink>
              )}
              {isPlurallApp && !isTeacher && sellPremiumToUser && (
                <ContextMenuButton
                  onClick={() => openWindowSecurely(getLinkToPlurallStore())}
                >
                  <Text>
                    <GetPremium />
                  </Text>
                </ContextMenuButton>
              )}
              {hasTutoringInactive && (
                <ContextMenuLink
                  onClick={() => {
                    analyticsLogUserClickedActivateTutoringNavbar();
                    closeDrawer();
                  }}
                  to={linkToTutoringActivation}
                >
                  <Text>
                    <ActivateTutoring />
                  </Text>
                </ContextMenuLink>
              )}
              {!isPlurallApp && !isTeacher && sellUpgradeToUser && (
                <ContextMenuLink onClick={closeDrawer} to={linkToWhatIsPremium}>
                  <Text>
                    <UpgradePremium />
                  </Text>
                </ContextMenuLink>
              )}
            </Stack>

            <Divider direction="horizontal" />

            <Stack>
              <ContextMenuLink onClick={closeDrawer} to={linkToSettings()}>
                {{
                  icon: (
                    <Icon
                      iconName="cog"
                      size="medium"
                      accessibilityLabel={null}
                      tone="neutral"
                    />
                  ),
                  text: <Text>Settings</Text>
                }}
              </ContextMenuLink>
              <DarkModeMenuButton />

              {showRegionLink && (
                <ContextMenuLink
                  onClick={closeDrawer}
                  to={MODAL_ROUTES.CHOOSE_REGION.path}
                  state={MODAL_ROUTES.CHOOSE_REGION.buildState({
                    background: location
                  })}
                >
                  {{
                    icon: (
                      <Icon
                        iconName="globe"
                        size="medium"
                        accessibilityLabel={null}
                        tone="neutral"
                      />
                    ),
                    text: <Text>Region</Text>
                  }}
                </ContextMenuLink>
              )}

              {!isPlurallApp && (
                <ContextMenuLink
                  onClick={closeDrawer}
                  to={linkToDashboardFeedback()}
                >
                  {{
                    icon: (
                      <Icon
                        iconName="megaphone"
                        size="medium"
                        accessibilityLabel={null}
                        tone="neutral"
                      />
                    ),
                    text: <Text>Feedback</Text>
                  }}
                </ContextMenuLink>
              )}

              {!isPlurallApp && (
                <ContextMenuLink
                  to={linkToDashboardInviteParentsModal()}
                  onClick={closeDrawer}
                >
                  {{
                    icon: (
                      <Icon
                        iconName="group"
                        size="medium"
                        accessibilityLabel={null}
                        tone="neutral"
                      />
                    ),
                    text: <Text>Invite parent</Text>
                  }}
                </ContextMenuLink>
              )}

              <ContextMenuButton
                onClick={() =>
                  isPlurallApp
                    ? dispatch(setPlurallHelpCentreModalOpen())
                    : openWindowSecurely(linkToStudentHelpSection(locale))
                }
              >
                {{
                  icon: (
                    <Icon
                      iconName="help"
                      size="medium"
                      accessibilityLabel={null}
                      tone="neutral"
                    />
                  ),
                  text: <Text>Help centre</Text>
                }}
              </ContextMenuButton>

              {/* TODO Look into if we will replace this with SOMOS equivalent */}
              {!isPlurallApp && (
                <ContextMenuButton
                  onClick={() =>
                    openWindowSecurely(linkToHealthResourcesArticle(locale))
                  }
                >
                  {{
                    icon: (
                      <Icon
                        iconName="heart"
                        size="medium"
                        accessibilityLabel={null}
                        tone="neutral"
                      />
                    ),
                    text: <Text>Mental wellbeing</Text>
                  }}
                </ContextMenuButton>
              )}
            </Stack>

            <Divider direction="horizontal" />

            {appVersionUpdateReady && [
              <Stack>
                <ContextMenuButton onClick={updateAppVersion}>
                  {{
                    icon: (
                      <Icon
                        iconName="reassign"
                        size="medium"
                        accessibilityLabel={null}
                        tone="neutral"
                      />
                    ),
                    text: (
                      <Text>
                        {appVersionIsUpdating ? (
                          <>Updating...</>
                        ) : (
                          <>Update Seneca</>
                        )}
                      </Text>
                    )
                  }}
                </ContextMenuButton>
              </Stack>,
              <Divider direction="horizontal" />
            ]}

            <ContextMenuButton onClick={signOut}>
              {{
                icon: (
                  <Icon
                    iconName="leave"
                    size="medium"
                    accessibilityLabel={null}
                    tone="neutral"
                  />
                ),
                text: <Text>Logout</Text>
              }}
            </ContextMenuButton>
          </Stack>
        )
      }}
    </NavbarSideDrawer>
  );
}
